import { defineStore } from 'pinia'

import { checkSavedCard } from '@/utils/checkSavedCard'
import { cropNumberOfCard } from '@/utils/cropNumberOfCard'

import { StoreId } from '@/store'
import { useAddressesStore } from '@/store/addresses/addresses'
import { useAddressSystemStore } from '@/store/addressSystem'
import { BasketState, PaymentOrderCheckout } from '@/store/basket/basket.types'
import { getBasketState } from '@/store/basket/basket.utils'
import { useBasketChangeStore } from '@/store/basket/basket-change'
import { useBasketPointStore } from '@/store/basket/basket-point'
import { useBasketUnavailableStore } from '@/store/basket/basket-unavailable'
import { useToggleStore } from '@/store/toggle/toggle'
import { useUserStore } from '@/store/user/user'

import {
  AddOrSetProductPayload,
  Cost,
  DeleteGiftPayload,
  DeliveryMethods,
  Gift,
  GiftPayload,
  GiftTypes,
  SetDeliveryMethodPayload,
  SetDeliveryMethodV5BasketPayload,
  SetDeliveryMethodV5Payload,
} from '@/api/modules/basket/types'
import {
  ConflictError,
  ServerError,
  Success,
  UnprocessableEntityError,
} from '@/api/results'
import { AdditiveItem } from '@/types/api/api-v3/basket'
import {
  BasketPayment,
  PaymentMethodType,
} from '@/types/api/api-v3/paymentMethods'

const defaultState: BasketState = {
  isLoaded: false,
  isLoading: false,
  error: null,
  data: {
    totalPrice: 0,
    totalAmount: 0,
    amountToPay: 0,
    itemsCount: 0,
    items: [],
    additives: [],
    maxNumberOfPersons: 0,
    persons: {
      errorMessage: '',
      helpMessage: '',
      maxNumber: 0,
      message: '',
      number: 0,
    },
    promocode: {
      discountAmount: 0,
      title: '',
      value: '',
      valid: true,
      error: '',
      type: null,
    },
    bonuses: {
      toSpendAmount: 0,
      maxSpendAmount: 0,
      toAccrueAmount: 0,
      totalAmount: 0,
      errorMessage: '',
      message: '',
    },
    delivery: {
      cost: null,
      method: DeliveryMethods.COURIER,
      order_address: null,
      addresses: {
        [DeliveryMethods.COURIER]: null,
        [DeliveryMethods.HALL]: null,
        [DeliveryMethods.PICKUP]: null,
      },
      pointId: null,
    },
    gifts: [],
    selectedPaymentMethod: null,
    note: '',
    change: 0,
    isExtra: false,
    proposition: {
      timeToLive: 0,
      popupText: '',
      items: [],
    },
    clientPacks: [],
    doNotCallMeBack: false, // По дефолту нужно перезвонить
  },
}

export const useBasketStore = defineStore(StoreId.BASKET, {
  state: (): BasketState => defaultState,
  actions: {
    async fetchBasket() {
      const { setLoadingWidget } = useAddressSystemStore()
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()

      this.isLoading = true

      setLoadingWidget(true)

      const result = await $apiService.basket.fetchBasket(this.$state, isMobile)

      setLoadingWidget(false)

      if (result instanceof Success) {
        this.isLoading = false
        this.isLoaded = true
        // @ts-ignore
        this.data = result.data

        return
      }

      this.isLoading = false
      this.isLoaded = true
      // @ts-ignore
      this.error = result
    },

    async addProduct(productId: number, productPrice: number) {
      try {
        const { $apiService, $metrics } = useNuxtApp()
        const { isMobile } = useDevice()
        const { featureToggle } = useToggleStore()

        const marketingSectionHeader = isMobile ? 'new_site_mobile' : 'new_site'
        const requestPayload: AddOrSetProductPayload = {
          product_id: productId,
          quantity: 1,
        }

        if (featureToggle.menu_by_points_enabled) {
          requestPayload.product_price = productPrice
        }

        const result = await $apiService.basket.addProduct(requestPayload, {
          headers: {
            'Marketing-Section': marketingSectionHeader,
          },
        })

        if (result instanceof Success) {
          const data = result.data

          if (!data) return

          this.setBasket({
            isLoaded: true,
            isLoading: false,
            data: getBasketState(data, this.$state, defaultState),
            error: null,
          })

          // Блюдо добавлено в корзину
          $metrics?.eventSendGoal({ ym: 'add_to_basket' })
        }
      } catch (error) {
        this.setBasket({
          error,
        })
      }
    },

    async setProduct({
      product_id,
      quantity,
      productPrice,
    }: {
      product_id: number
      quantity: number
      productPrice?: number | null
    }) {
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()
      const { featureToggle } = useToggleStore()

      const requestPayload: AddOrSetProductPayload = {
        product_id,
        quantity,
      }

      if (featureToggle.menu_by_points_enabled) {
        requestPayload.product_price = productPrice
      }

      const result = await $apiService.basket.setProduct(
        this.$state,
        requestPayload,
        isMobile,
      )

      if (result instanceof Success) {
        this.setBasket({
          isLoading: false,
          isLoaded: true,
          // @ts-ignore
          data: result.data,
          error: null,
        })

        return
      }

      if (result instanceof UnprocessableEntityError) {
        // @ts-ignore
        this.setBasket({
          isLoading: false,
          isLoaded: true,
          error: result.data,
        })

        return
      }

      this.setBasket({
        // @ts-ignore
        error: result,
      })
    },

    async clearBasket() {
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()
      const basketChangeStore = useBasketChangeStore()
      const { clearBasketUnavailableState } = useBasketUnavailableStore()

      const result = await $apiService.basket.clear(isMobile)

      if (result instanceof Success) {
        this.clearBasketData()

        basketChangeStore.resetSelectedProducts()
        clearBasketUnavailableState()

        // Перезапрашиваем корзину, чтобы получить delivery, который может поменяться
        await this.fetchBasket()

        return
      }

      this.setBasket({
        error: result,
      })
    },

    async orderCheckout() {
      const { $apiService, $metrics } = useNuxtApp()
      const { isMobile } = useDevice()
      const basketChangeStore = useBasketChangeStore()
      const { clearBasketUnavailableState } = useBasketUnavailableStore()

      const payment_method = checkSavedCard(this.data.selectedPaymentMethod)
        ? PaymentMethodType.FEATURE_SAVED_CARD
        : this.data.selectedPaymentMethod?.type

      const formData: PaymentOrderCheckout = {
        number_of_persons: this.data.persons.number,
        payment_method,
        notes: this.data.note,
        change: this.data.change,
        do_not_call_me_back: this.data.doNotCallMeBack,
        delivery_cost: this.data.delivery?.cost?.value || 0,
      }

      if (this.data.persons.maxNumber === 0) delete formData.number_of_persons

      const result = await $apiService.basket.orderCheckout(formData, isMobile)

      if (result instanceof ConflictError) return result

      if (result instanceof Success) {
        basketChangeStore.resetSelectedProducts()
        clearBasketUnavailableState()

        // Заказ успешно оформлен
        $metrics?.eventSendGoal({ ym: 'order_confirmed' })

        return result
      }

      if (result instanceof UnprocessableEntityError) {
        // @ts-ignore
        this.setBasket({ error: result })
        return result
      }
      // @ts-ignore
      this.setBasket({ error: result })
    },

    setBasket(basket: Partial<BasketState>) {
      Object.assign(this.$state, basket)

      // Если очистили корзину, то очищаем и кол-во выбранных товаров
      if (!this.$state.data?.items.length) {
        const basketChangeStore = useBasketChangeStore()
        const { clearBasketUnavailableState } = useBasketUnavailableStore()

        basketChangeStore.resetSelectedProducts()
        clearBasketUnavailableState()
      }
    },

    async promocodeReset() {
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()

      const result = await $apiService.basket.resetPromocode(
        this.$state,
        isMobile,
      )

      if (result instanceof Success) {
        this.setBasket({
          isLoaded: true,
          data: result.data,
          error: null,
        })

        return result
      }

      this.setBasket({
        error: result,
      })
    },

    async promocodeApply(bodyRequest: { value: string }) {
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()

      const result = await $apiService.basket.applyPromocode(
        this.$state,
        bodyRequest,
        isMobile,
      )

      if (result instanceof Success) {
        this.setBasket({
          isLoaded: true,
          data: result.data,
          error: null,
        })

        return result
      }

      return result
    },
    async setGift(gift: GiftPayload) {
      if (!gift) return

      const { $apiService } = useNuxtApp()

      const result = await $apiService.basket.setGift(this.$state, gift)

      if (result instanceof Success) {
        this.setBasket({
          data: result.data,
        })
      }

      return result
    },
    async deleteGift(gift: DeleteGiftPayload) {
      if (!gift) return

      const { $apiService } = useNuxtApp()

      const result = await $apiService.basket.deleteGift(this.$state, gift)

      if (result instanceof Success) {
        this.setBasket({
          data: result.data,
        })
      }

      return result
    },
    async setTime(time: { delivery_at_client: boolean; delivery_at: string }) {
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()

      const result = await $apiService.basket.setTime(
        this.$state,
        time,
        isMobile,
      )

      if (result instanceof Success) {
        this.setBasket({
          isLoaded: true,
          data: result?.data,
          error: null,
        })
      }
    },

    clearBasketData() {
      this.setBasket({
        data: defaultState.data,
        error: defaultState.error,
        isLoading: defaultState.isLoading,
      })
    },

    setSelectedPaymentMethod(paymentMethod: BasketPayment | null) {
      this.data.selectedPaymentMethod = paymentMethod
    },

    setChange(change: number) {
      this.data.change = change
    },

    async applyBonuses(bodyRequest: { amount: string | number }) {
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()
      const result = await $apiService.basket.applyBonuses(
        this.$state,
        bodyRequest,
        isMobile,
      )

      if (result instanceof Success) {
        this.setBasket({
          isLoaded: true,
          data: result.data,
          error: null,
        })

        return result
      }

      if (
        !(result instanceof Success) &&
        !(result instanceof UnprocessableEntityError)
      ) {
        this.data.bonuses.errorMessage = 'Не удалось загрузить бонусы'
      }

      return result
    },

    setNote(note: string) {
      this.data.note = note
    },

    setDeliveryCost(deliveryCost: Partial<Cost>) {
      if (this.data.delivery.cost) {
        Object.assign(this.data.delivery.cost, deliveryCost)
      }
    },

    setDeliveryOrderAddress(address: string) {
      this.data.delivery.order_address = address
    },

    setDeliveryMethodInState(deliveryMethod: DeliveryMethods) {
      this.data.delivery.method = deliveryMethod
    },

    async setDeliveryMethod(
      bodyRequest: SetDeliveryMethodPayload | SetDeliveryMethodV5Payload,
    ) {
      const { $apiService, $metrics } = useNuxtApp()
      const { isMobile } = useDevice()
      const { setLoadingWidget } = useAddressSystemStore()
      const { menuByPointsEnabled } = useToggleStore()

      setLoadingWidget(true)

      const result = menuByPointsEnabled
        ? await $apiService.basket.setDeliveryMethodV5(this.$state, bodyRequest)
        : await $apiService.basket.setDeliveryMethod(
            this.$state,
            bodyRequest,
            isMobile,
          )

      setLoadingWidget(false)

      if (result instanceof Success) {
        // @ts-ignore
        this.setBasket({
          isLoaded: true,
          ...result,
          error: null,
        })

        $metrics?.eventSendGoal({ ym: 'address_save__in_basket' })

        return
      }

      // @ts-ignore
      this.setBasket({ error: result })
    },

    async setBasketDeliveryV5(data: SetDeliveryMethodV5BasketPayload) {
      const userStore = useUserStore()
      const isAuthenticated = computed(() => userStore.isAuthenticated)
      const isCourier = data.delivery_method === DeliveryMethods.COURIER

      const deliveryMethodPayload: SetDeliveryMethodV5Payload = {
        delivery_method: data.delivery_method,
      }

      if (!isCourier) {
        // Для самовывоза или в ресторане берем point_id
        deliveryMethodPayload.point_id = data.point_id
      } else if (isAuthenticated.value) {
        // Для доставки авторизованного пользователя берем address_id
        deliveryMethodPayload.address_id = data.address_id
      } else if (data.coordinates) {
        // Для доставки неавторизованного пользователя берем координаты выбранной точки
        const [lon, lat] = data.coordinates

        deliveryMethodPayload.geo_point = {
          latitude: lat,
          longitude: lon,
        }
      }

      await this.setDeliveryMethod(deliveryMethodPayload)
    },

    setAddress({ type, id }: { type: DeliveryMethods; id: number }) {
      this.data.delivery.addresses[type] = id
    },

    async setProposition(propositionItem: {
      proposition_item_id: number
      quantity?: number
    }) {
      const { $apiService } = useNuxtApp()
      const { isMobile } = useDevice()

      const result = await $apiService.basket.setProposition(
        this.$state,
        propositionItem,
        isMobile,
      )

      if (result instanceof Success) {
        // @ts-ignore
        this.setBasket({
          isLoaded: true,
          ...result,
        })
      }
    },

    setDoNotCallMeBack(doNotCallMeBack: boolean) {
      this.data.doNotCallMeBack = doNotCallMeBack
    },
  },
  getters: {
    selectedAddress: (state) => {
      const addressesStore = useAddressesStore()
      const pointStore = useBasketPointStore()

      switch (state.data.delivery.method) {
        case DeliveryMethods.COURIER: {
          const selectedAddressId =
            state.data.delivery.addresses[DeliveryMethods.COURIER]

          const selectedAddress = addressesStore.addresses.data.find(
            (address) => address.id === selectedAddressId,
          )

          if (!selectedAddress) return ''

          return formatToFullVisibleAddress(selectedAddress)
        }
        case DeliveryMethods.HALL: {
          const selectedPointId =
            state.data.delivery.addresses[DeliveryMethods.HALL]

          return pointStore.data.find((point) => point.id === selectedPointId)
            ?.address
        }
        case DeliveryMethods.PICKUP: {
          const selectedPointId =
            state.data.delivery.addresses[DeliveryMethods.PICKUP]

          return pointStore.data.find((point) => point.id === selectedPointId)
            ?.address
        }
        default:
          return undefined
      }
    },
    basketIsEmpty: ({ data, error }) => {
      return (
        data.itemsCount === 0 ||
        error?.response?.data?.detail?.includes('Корзина пуста')
      )
    },
    basketIsDisabled: ({ error }) =>
      error instanceof ServerError ||
      error?.response?.data?.detail?.includes('Приём заказов приостановлен'),
    isProductInBasket:
      ({ data }) =>
      (productId: number) => {
        return data.items.find((product) => product.product_id === productId)
      },
    clientPacksCount: ({ data }) => {
      let sum = 0

      if (!data.clientPacks) return 0

      data.clientPacks.forEach((item) => (sum = sum + item.total_quantity))

      return sum
    },
    giftsNoChoice: (state): Gift[] =>
      state.data.gifts.filter((gift) => gift.type !== GiftTypes.CHOICE),
    giftsOfChoice: (state): Gift[] => {
      const filteredGifts = state.data.gifts.filter(
        (gift) => gift.should_choose,
      )

      if (!filteredGifts || filteredGifts.length === 0) return []

      const firstGiftsOfChoice = filteredGifts.shift()

      return [firstGiftsOfChoice]
    },
    getSelectedGift: (state): GiftPayload | undefined => {
      // из-за BasketState не может определить геттер giftsOfChoice
      // @ts-ignore
      const giftsOfChoice: Gift[] = state.giftsOfChoice

      if (giftsOfChoice.length === 0) return

      const firstGiftList = giftsOfChoice[0]

      if (!firstGiftList?.rule_id) return

      const selectedProduct = firstGiftList?.products.find(
        ({ selected }) => selected,
      )

      if (!selectedProduct) return

      return {
        rule_id: firstGiftList.rule_id,
        product_id: selectedProduct.product_id,
      }
    },
    clientAdditivesCount: (state) => {
      if (state.data.clientPacks === undefined) return 0

      return state.data.additives.reduce(
        (accumulator, additive) =>
          (accumulator = accumulator + additive.total_quantity),
        0,
      )
    },

    promocodeIsValid: (state) => state.data.promocode?.valid,

    basketAddressSelected: (state) =>
      Object.values(state.data.delivery.addresses).some((address) => address),
    checkAccessEnterPromo: (state) => {
      return !!state.data.bonuses.toSpendAmount
    },
    bonusesIsApplied: (state) => state.data.bonuses.toSpendAmount !== 0,
    basketSelectedDeliveryMethod: (state) => {
      const methods = {
        courier: 'Адрес доставки',
        pickup: 'Самовывоз',
        hall: 'В ресторане',
      }

      if (state.data.delivery.method) {
        return methods[state.data.delivery.method]
      }

      return ''
    },
    addedAdditives: (state) =>
      state.data.additives?.reduce<AdditiveItem[]>(
        (acc, additive) =>
          additive.total_quantity === 0 ? acc : [...acc, additive],
        [],
      ),

    addedClientPacks: (state) =>
      state.data.clientPacks?.filter((pack) => pack.total_quantity !== 0) || [],

    basketSelectedPaymentMethod: (state) => {
      const { selectedPaymentMethod, delivery } = state.data

      // Если способ оплаты не выбран
      if (!selectedPaymentMethod) return 'Не выбрано'

      // Если это карта, то возвращаем карту
      if (checkSavedCard(selectedPaymentMethod)) {
        return `Картой онлайн ${cropNumberOfCard(
          selectedPaymentMethod?.card_number,
          8,
        )}`
      }
      // Исключение для оплаты картой курьеру
      if (
        delivery.method === DeliveryMethods.COURIER &&
        selectedPaymentMethod.type === PaymentMethodType.CARD
      )
        return 'Картой курьеру'

      const methods = {
        cash: 'Наличные',
        card: 'Картой в ресторане',
        sberbank: 'Онлайн',
        cloud_payments: 'Онлайн',
        pay_selection: 'Онлайн',
        sbp_sberbank: 'Оплата через СБП',
      }

      return methods[selectedPaymentMethod.type]
    },
    checkSelectedSavedCard: (state) => {
      const { selectedPaymentMethod } = state.data

      return !!selectedPaymentMethod && checkSavedCard(selectedPaymentMethod)
    },

    isExtraBasket: ({ data }) => data.isExtra,

    // Количество товаров в корзине по каждому из id модификаций
    basketSelectedProducts: (state) => {
      const result: Record<number, number> = {}

      for (const product of state.data.items) {
        result[product.product_id] = product.quantity
      }

      return result
    },
  },
  // Сохраняем note в localStorage для сохранения после обновления
  persist: {
    pick: ['data.note'],
    storage: piniaPluginPersistedstate.localStorage(),
  },
})
